import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatMs } from "../../logic/utils";
import { TimelineTime } from "../../types";
import { TIMELINE_PADDING, TRACK_ICON_WIDTH } from "./trackTheme";

export const RULER_HEIGHT = 32;
export const MAX_CANVAS_PX = 32767;
const MIN_LABEL_PX = 64;
const LABEL_STEPS_SECONDS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];

export function getRulerRenderConfig({
  trackWidth,
  pixelsPerMs,
  devicePixelRatio,
}: {
  trackWidth: number;
  pixelsPerMs: number;
  devicePixelRatio: number;
}) {
  const dpr =
    Number.isFinite(devicePixelRatio) && devicePixelRatio > 0
      ? devicePixelRatio
      : 1;
  const logicalWidthLimit = MAX_CANVAS_PX / dpr;
  const renderWidth = Math.max(Math.min(trackWidth, logicalWidthLimit), 1);
  const renderHeight = Math.min(RULER_HEIGHT, MAX_CANVAS_PX / dpr);
  const renderScale = trackWidth > 0 ? renderWidth / trackWidth : 1;

  return {
    dpr,
    renderWidth,
    renderHeight,
    renderScale,
    scaledPixelsPerMs: pixelsPerMs * renderScale,
  };
}

type RulerProps = {
  totalDuration: TimelineTime;
  pixelsPerMs: number;
  trackWidth: number;
};

function RulerComponent({
  totalDuration,
  pixelsPerMs,
  trackWidth,
}: RulerProps) {
  const pixelsPerSecond = pixelsPerMs * 1000;
  const [devicePixelRatioState, setDevicePixelRatioState] = useState(1);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rootRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMeasuredWidth(entry.contentRect.width);
      }
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  const effectiveTrackWidth = Math.max(
    0,
    measuredWidth - TRACK_ICON_WIDTH - TIMELINE_PADDING,
    trackWidth
  );
  const effectiveTotalDuration =
    effectiveTrackWidth / Math.max(pixelsPerMs, 0.0001);

  const labelStepSeconds = useMemo(() => {
    const target = MIN_LABEL_PX / Math.max(pixelsPerSecond, 0.0001);
    const step =
      LABEL_STEPS_SECONDS.find((candidate) => candidate >= target) ??
      LABEL_STEPS_SECONDS[LABEL_STEPS_SECONDS.length - 1];
    return step;
  }, [pixelsPerSecond]);

  const formatRulerLabel = useCallback((ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      const mm = minutes.toString().padStart(2, "0");
      const ss = seconds.toString().padStart(2, "0");
      return `${hours}h${mm}m${ss}s`;
    }
    return formatMs(ms, "MMmSSs");
  }, []);

  useEffect(() => {
    const next = Number.isFinite(window.devicePixelRatio)
      ? window.devicePixelRatio
      : 1;
    setDevicePixelRatioState(next > 0 ? next : 1);
  }, []);

  const segments = useMemo(() => {
    const limit = Math.max(1, MAX_CANVAS_PX / devicePixelRatioState);
    const parts: { width: number; offsetPx: number }[] = [];
    let remaining = effectiveTrackWidth;
    let offsetPx = 0;
    while (remaining > 0) {
      const width = Math.min(remaining, limit);
      parts.push({ width, offsetPx });
      remaining -= width;
      offsetPx += width;
    }
    return parts;
  }, [devicePixelRatioState, effectiveTrackWidth]);

  const fullWidth = trackWidth + TRACK_ICON_WIDTH + TIMELINE_PADDING;
  const effectiveFullWidth = Math.max(fullWidth, measuredWidth);

  return (
    <div
      ref={rootRef}
      className="relative flex min-w-full"
      style={{
        height: RULER_HEIGHT,
        width: fullWidth,
      }}
    >
      <div
        className="sticky left-0 shrink-0 mr-1 z-10 bg-background-elevated-2"
        style={{ width: TRACK_ICON_WIDTH, height: RULER_HEIGHT }}
      />
      <div className="relative flex-1 pr-10">
        {segments.map(({ width, offsetPx }, index) => (
          <RulerSegment
            key={`${offsetPx}-${index}`}
            width={width}
            offsetPx={offsetPx}
            totalDuration={effectiveTotalDuration}
            pixelsPerMs={pixelsPerMs}
            labelStepSeconds={labelStepSeconds}
            formatRulerLabel={formatRulerLabel}
            devicePixelRatioState={devicePixelRatioState}
          />
        ))}
      </div>
      <div
        className="absolute bottom-0 left-0 min-w-full z-20 h-px bg-text-primary/[.08]"
        style={{
          width: effectiveFullWidth,
        }}
      />
    </div>
  );
}

type RulerSegmentProps = {
  width: number;
  offsetPx: number;
  totalDuration: TimelineTime;
  pixelsPerMs: number;
  labelStepSeconds: number;
  formatRulerLabel: (ms: number) => string;
  devicePixelRatioState: number;
};

function RulerSegmentComponent({
  width,
  offsetPx,
  totalDuration,
  pixelsPerMs,
  labelStepSeconds,
  formatRulerLabel,
  devicePixelRatioState,
}: RulerSegmentProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(totalDuration) ||
      !Number.isFinite(pixelsPerMs) ||
      width <= 0 ||
      totalDuration <= 0 ||
      pixelsPerMs <= 0
    ) {
      console.warn("Ruler: Invalid parameters", {
        width,
        totalDuration,
        pixelsPerMs,
      });
      return;
    }

    const { dpr, renderWidth, renderHeight, scaledPixelsPerMs } =
      getRulerRenderConfig({
        trackWidth: width,
        pixelsPerMs,
        devicePixelRatio: devicePixelRatioState,
      });

    const canvasWidth = Math.round(renderWidth * dpr);
    const canvasHeight = Math.round(renderHeight * dpr);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    canvas.style.width = `${renderWidth}px`;
    canvas.style.height = `${RULER_HEIGHT}px`;
    canvas.style.left = `${offsetPx}px`;
    canvas.style.top = "0px";
    canvas.style.position = "absolute";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, renderWidth, renderHeight);

    const style = getComputedStyle(canvas);
    const textColor = style.getPropertyValue("--color-text-primary")?.trim();
    const lineColor = textColor || "#9CA3AF";

    ctx.strokeStyle = lineColor;
    ctx.fillStyle = textColor || "#9CA3AF";
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = 1;
    ctx.textBaseline = "top";
    ctx.textAlign = "center";
    ctx.font = "12px ui-sans-serif, system-ui, -apple-system, sans-serif";

    const majorMs = labelStepSeconds * 1000;
    const minorMs = majorMs / 10;
    const startMs = offsetPx / pixelsPerMs;
    const endMs = Math.min(totalDuration, startMs + width / pixelsPerMs);
    const divisor = minorMs * scaledPixelsPerMs;

    if (divisor <= 0 || !Number.isFinite(divisor)) {
      console.warn("Ruler: Invalid divisor", { minorMs, pixelsPerMs });
      return;
    }

    const firstIndex = Math.max(0, Math.ceil(startMs / minorMs));
    const maxTicks = Math.min(
      Math.ceil(((endMs - startMs) * scaledPixelsPerMs) / divisor) + 2,
      10000
    );

    for (let i = 0; i <= maxTicks; i += 1) {
      const tickIndex = firstIndex + i;
      const timeMs = Math.round(tickIndex * minorMs);
      if (timeMs > endMs) break;
      const x = Math.round((timeMs - startMs) * scaledPixelsPerMs);

      if (!Number.isFinite(x) || x < -1 || x > renderWidth + 1) continue;

      const isMajor = tickIndex % 10 === 0;
      const tickHeight = isMajor ? 6 : 4;

      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, tickHeight);
      ctx.stroke();

      if (isMajor && timeMs > 0) {
        ctx.fillText(formatRulerLabel(timeMs), x, tickHeight + 2);
      }
    }
    ctx.globalAlpha = 1;
  }, [
    devicePixelRatioState,
    formatRulerLabel,
    labelStepSeconds,
    offsetPx,
    pixelsPerMs,
    totalDuration,
    width,
  ]);

  return <canvas ref={canvasRef} />;
}

const RulerSegment = memo(RulerSegmentComponent);

export const Ruler = memo(RulerComponent);
